<template>
    <div class="equipment-form" v-loading="loading">

        <PageTitle
            :text="capitalizeFirstLetter(pageTitle)"
        >
        </PageTitle>

        <div class="padding">

            <div style="padding-bottom: 20px">
                <p>{{ pageDescription }}</p>
            </div>

            <div
                class="form-container padding shadow"
                style="padding-top:40px; margin-bottom:20px;">
                <ns-loader
                    :fetch-timer="null"
                    :to-load="toLoad"
                    :on-to-load-change-options="{triggerRefresh: true}"
                    @dataFetched="onDataFetched"
                    ref="equipmentFormLoader"
                >
                    <template slot="content">
                <el-form
                    ref="equipmentForm"
                    :model="form"
                    :rules="rules"
                    label-position="top"
                    label-width="280px"
                    class="site-form"
                >
                    <div class="form-part">
                        <div>
                            <h2 class="step-title">Déclaration {{ commonTypeVerbose.article }} {{ equipmentTypeVerbose}}</h2>
                        </div>
                        <div
                            class="part"
                        >
                            <el-form-item
                                label="Nom"
                                prop="name"
                            >
                                <el-input
                                    v-model="form.name"
                                    style="width:100%">
                                </el-input>
                            </el-form-item>
                            <el-form-item
                                label="Types de machine"
                                prop="equipment_type"
                            >
                                <el-select
                                    v-model="form.equipment_type"
                                    style="width:100%"
                                    @change="resetEquipmentTypeRelatedFields"
                                >
                                    <el-option
                                        v-for="equipment in equipmentList"
                                        :key="equipment.key"
                                        :label="equipment.value"
                                        :value="equipment.key">
                                    </el-option>
                                </el-select>
                            </el-form-item>
                            <el-form-item
                                label="Description"
                                prop="description"
                            >
                                <el-input
                                    v-model="form.description"
                                    style="width:100%">
                                </el-input>
                            </el-form-item>
                            <el-form-item
                                v-if="form.equipment_type === 'motor'"
                                label="Types de moteur"
                                prop="motor_type"
                            >
                                <el-select
                                    v-model="form.motor_type"
                                    style="width:100%"
                                >
                                    <el-option
                                        v-for="motor in motorList"
                                        :key="motor.key"
                                        :label="motor.value"
                                        :value="motor.key">
                                    </el-option>
                                </el-select>
                            </el-form-item>
                        </div>
                        <div class="part">
                            <el-form-item
                                label="Installation d’un compteur énergie"
                                prop="energy_meter"
                            >
                                <el-select
                                    v-model="form.energy_meter"
                                    style="width:100%"
                                    @change="resetEnergyMeterRelatedFields"
                                >
                                    <el-tooltip
                                        v-for="meter in energyMetersEquipment"
                                        :key="meter.key"
                                        :content="siblings.length === 0 ? `Un compteur virtuel ne peut être utilisé sans au moins ${commonTypeVerbose.article2} ${equipmentTypeVerbose} au même niveau` : ''"
                                        :disabled="siblings.length > 0 || meter.key !== 'virtual_meter'"
                                        placement="top"
                                        effect="light"
                                    >
                                        <el-option
                                            :label="meter.value"
                                            :value="meter.key"
                                            :disabled="meter.key === 'virtual_meter' && siblings.length === 0"
                                            >
                                        </el-option>
                                    </el-tooltip>
                                </el-select>
                            </el-form-item>
                            <el-form-item
                                label="Numéro du compteur"
                                prop="energy_meter_name"
                            >
                                <el-input
                                    v-model.number="form.energy_meter_name"
                                    style="width:100%"
                                >
                                    <template slot="prepend">{{ energyMetersPrefix(form.energy_meter) }}</template>
                                </el-input>
                            </el-form-item>
                            <el-form-item
                                label="Règle du jeu"
                                prop="rules"
                            >
                                <template #label>
                                    <span>
                                        Règle du jeu
                                        <el-tooltip
                                            placement="top"
                                            effect="light"
                                        >
                                            <div slot="content">
                                               La règle ne doit s'exprimer qu'avec le nom des compteurs du parent <br>
                                               et des moteurs ou machines du même niveau hiérarchique que l'équipement actuel et <br> 
                                                suivre un de ces formats : <br>
                                                <br>
                                               <ul>
                                                <li>parent - équipement1 - équipement2</li>
                                                <li>parent + (équipement1 - équipement2)</li>
                                               </ul>
                                            </div>
                                            <i class="el-icon-info"></i>
                                        </el-tooltip>
                                    </span>
                                </template>
                                <el-input
                                    v-model="form.rules"
                                    :disabled="!usesVirtualEnergyMeter"
                                    style="width:100%">
                                </el-input>
                            </el-form-item>
                        </div>
                    </div>
                    <div class="form-part">
                        <div>
                            <h2 class="step-title">Plaque signalétique</h2>
                        </div>
                        <div
                            class="part bottom-part"
                        >
                            <div class="part">
                                <el-form-item
                                    label="Puissance (kW)"
                                    prop="power"
                                >
                                    <el-input-number
                                        v-model.number="form.power"
                                        :controls="false"
                                        :min="0"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                                
                                <el-form-item
                                    label="Tension (V)"
                                    prop="tension"
                                >
                                    <el-input-number
                                        v-model.number="form.tension"
                                        :controls="false"
                                        :min="0"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                                <template
                                    v-if="form.equipment_type === 'motor'"
                                >
                                    <el-form-item
                                        label="Courant (A)"
                                        prop="current"
                                    >
                                        <el-input-number
                                            v-model.number="form.current"
                                            :controls="false"
                                            :min="0"
                                            style="width:100%">
                                        </el-input-number>
                                    </el-form-item>
                                    <el-form-item
                                        label="Vitesse (tr/min)"
                                        prop="speed"
                                    >
                                        <el-input-number
                                            v-model.number="form.speed"
                                            :controls="false"
                                            :min="0"
                                            style="width:100%">
                                        </el-input-number>
                                    </el-form-item>
                                </template>
                                <template
                                    v-if="form.equipment_type === 'machine'"
                                >
                                    <div class="part sub-part">
                                        <el-form-item
                                            label="Cosphi"
                                            prop="cos_phi_known"
                                        >
                                            <el-radio-group
                                                v-model="form.cos_phi_known"
                                                @change="resetCosphiRelatedFields"
                                            >
                                                <el-radio
                                                    :label="true">
                                                    Connu
                                                </el-radio>
                                                <el-radio
                                                    :label="false">
                                                    Inconnu
                                                </el-radio>
                                            </el-radio-group>
                                        </el-form-item>
                                        <el-form-item
                                            v-if="form.cos_phi_known"
                                            prop="cosphi"
                                        >
                                            <el-input-number
                                                v-model.number="form.cos_phi"
                                                :precision="2" 
                                                :controls="false"
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                        <el-form-item
                                            v-else
                                            prop="machine_type"
                                        >
                                            <el-select
                                                v-model="form.machine_type"
                                                style="width:100%"
                                                @change="updateCosPhi"
                                            >
                                                <el-option
                                                    v-for="cosphi in $store.state.cosphiLibrary"
                                                    :key="cosphi.key"
                                                    :label="`${cosphi.value} - ${cosphi.label}`"
                                                    :value="cosphi.key"
                                                >
                                                    <span style="float: left">{{ cosphi.label }}</span>
                                                    <span style="float: right; color: #8492a6; font-size: 13px">{{ cosphi.value }}</span>
                                                </el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>
                                </template>
                                <template
                                    v-else
                                >                            
                                    <el-form-item
                                        label="Cosphi"
                                        prop="cos_phi"
                                    >
                                        <el-input-number
                                            v-model.number="form.cos_phi"
                                            :precision="2" 
                                            :controls="false"
                                            :min="0"
                                            style="width:100%">
                                        </el-input-number>
                                    </el-form-item>
                                </template>
                            </div>
                            <div class="part">
                                <el-form-item
                                    label="Valeur d’équilibrage deuxième phase (%)"
                                    prop="second_phase_balancing_value"
                                >
                                    <el-input-number
                                        v-model.number="form.second_phase_balancing_value"
                                        :precision="2" 
                                        :controls="false"
                                        :disabled="usesWattMeter || usesVirtualEnergyMeter"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                                <el-form-item
                                    label="Valeur d’équilibrage troisième phase (%)"
                                    prop="third_phase_balancing_value"
                                >
                                    <el-input-number
                                        v-model.number="form.third_phase_balancing_value"
                                        :precision="2" 
                                        :controls="false"
                                        :disabled="usesWattMeter || usesVirtualEnergyMeter"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                                <el-form-item
                                    label="Puissance supplémentaire (kW)"
                                    prop="additional_power"
                                >
                                    <el-input-number
                                        v-model.number="form.additional_power"
                                        :controls="false"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                                <el-form-item
                                    label="Puissance à l'arrêt (kW)"
                                    prop="idle_power"
                                >
                                    <el-input-number
                                        v-model.number="form.idle_power"
                                        :controls="false"
                                        :min="0"
                                        style="width:100%">
                                    </el-input-number>
                                </el-form-item>
                            </div>
                        </div>
                    </div>
                    <el-form-item class="button-container">
                        <el-button
                            type="primary"
                            icon="el-icon-check"
                            @click="save">
                            Enregistrer
                        </el-button>
                        <el-button
                            icon="el-icon-close"
                            @click="leaveSite">
                            Annuler
                        </el-button>
                    </el-form-item>
                </el-form>
                    </template>
                </ns-loader>
            </div>
        </div>

    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { capitalizeFirstLetter } from '@/components/partials/utils';
import PageTitle from '@/components/partials/ui/PageTitle';
import FormHelp from '@/components/partials/ui/FormHelp';
import NsLoader from 'fundation-app-vuejs/src/components/partials/ns-vue-loaderV2';

export default {
    name: 'equipmentForm',
    components: {
        PageTitle,
        FormHelp,
        NsLoader
    },
    data () {
        let validateEnergyMeterName = (rule, value, callback)=>{
            if (value === '') {
                callback(new Error('Le numéro du compteur d\'énergie est obligatoire'));
            } else if(!Number.isInteger(parseInt(value))){
                callback(new Error('Le numéro du compteur doit être un entier'));
            } else if(
                this.currentEnergyMeterNames.includes(this.energyMeterFullName)
            ) {
                if (
                    this.currentEquipment !== null && this.energyMeterFullName === this.currentEquipment.energy_meter_name
                ){
                    callback()
                }else{
                    callback(new Error('Un compteur existe déjà avec ce nom'));
                }
            } else {
              callback();
            }
        };
        let validatePower = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 0 || value > 100000) {
                callback(new Error('La tension doit être comprise entre 0 et 100000 kW'));
            } else {
                callback();
            }
        };
        let validateTension = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 200 || value > 1000) {
                callback(new Error('La tension doit être comprise entre 200 et 1000 V'));
            } else {
                callback();
            }
        };
        let validateCurrent = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 0 || value > 10000) {
                callback(new Error('La tension doit être comprise entre 0 et 10000 A'));
            } else {
                callback();
            }
        };
        let validateSpeed = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (!((value >= 1400 && value <= 1500) || (value >= 2900 && value <= 3000))) {
                callback(new Error('La vitesse doit être comprise entre 1400 et 1500 tr/min ou entre 2900 et 3000 tr/min'));
            } else {
                callback();
            }
        };
        let validateCosPhi = (rules, value, callback) => {
            if (!Number.isFinite(value)) {
                callback(new Error('La valeur doit être un nombre à virgule'));
            } else if(value < 0 || value > 1) {
                callback(new Error('Le Cosphi doit être comprise entre 0 et 1'));
            } else {
                callback();
            }
        };
        let validateBalancingValue = (rules, value, callback) => {
            if(this.usesVirtualEnergyMeter || this.usesWattMeter){
                callback();
                return;
            }
            if (!Number.isFinite(value)) {
                callback(new Error('La valeur doit être un nombre à virgule'));
            } else if(value < -100 || value > 100) {
                callback(new Error('La valeur doit être comprise entre -100 et 100'));
            } else {
                callback();
            }
        };
        let validateAdditionalPower = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if(value < 0 || value > 1000000) {
                callback(new Error('La tension doit être comprise entre 0 et 1000000 kW'));
            } else {
                callback();
            }
        };
        let validateIdlePower = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 0 || value > 1000000) {
                callback(new Error('La tension doit être comprise entre 0 et 1000000 kW'));
            } else {
                callback();
            }
        };
        let validateRules = (rules, value, callback) => {
            if(!this.usesVirtualEnergyMeter){
                callback();
                return;
            }
            if (typeof value !== 'string') {
                callback(new Error('Règle manquante'));
                return;
            }
            const trimmedValue = value.trim();
            const hasVirtualMeter = /\bV\d+\b/.test(trimmedValue);
            if (hasVirtualMeter) {
                callback(new Error('Impossible d\'utiliser un autre compteur virtuel dans la règle'));
                return;
            }
            const regex =
            /^(?:A|WVAR|W|E|V)\d+\s*-\s*(?:(?:A|WVAR|W|E|V)\d+(?:\s*-\s*(?:A|WVAR|W|E|V)\d+)*|\((?:A|WVAR|W|E|V)\d+(?:\s*\+\s*(?:A|WVAR|W|E|V)\d+)*\))$/;
            if (!regex.test(trimmedValue)) {
                callback(new Error('Règle invalide'));
                return;
            }

            // Extra validation: first term must match parent's energy meter name
            const firstTermMatch = trimmedValue.match(/^((?:A|WVAR|W|E|V)\d+)/);
            if (firstTermMatch && this.parent && this.parent.energy_meter_name) {
                const firstTerm = firstTermMatch[1];
                if (firstTerm !== this.parent.energy_meter_name) {
                    callback(new Error('Le premier terme doit être le nom du compteur du parent'));
                    return;
                }
            }

            // Extra validation: every other term must be a sibling's energy meter name
            const allTerms = trimmedValue.match(/(?:A|WVAR|W|E|V)\d+/g) || [];
            const otherTerms = allTerms.slice(1); // skip the first term already validated
            if (otherTerms.length && Array.isArray(this.siblings)) {
                const siblingMeterNames = this.siblings
                    .map(s => s && s.energy_meter_name)
                    .filter(Boolean);

                const invalidOther = otherTerms.find(term => !siblingMeterNames.includes(term));
                if (invalidOther) {
                    callback(new Error('Chaque autre terme doit être le nom du compteur d\'un moteur ou d\'une machine au niveau actuel'));
                    return;
                }
            }

            callback();
        };
        return {
            loading: false,
            parent: null,
            siblings: [],
            equipments: [],
            form: {
                name: '',
                equipment_type: 'motor',
                description: '',
                motor_type: '',
                energy_meter: '',
                energy_meter_name: '',
                rules: '',
                power: 0.,
                tension: 0,
                current: 0,
                speed: 0,
                cos_phi_known: true,
                machine_type: '',
                cos_phi: 0.0,
                second_phase_balancing_value: 0.0,
                third_phase_balancing_value: 0.0,
                additional_power: 0,
                idle_power: 0,
                parent_site_uid: null,
                parent_area_uid: null,
            },
            rules: {
                name: [
                    { required: true, message: 'Le nom est obligatoire', trigger: 'blur' },
                    { min: 1, max: 20, message: 'La longueur maximale est de 20 caractères', trigger: 'change' }
                ],
                description: [
                    { required: true, message: 'La description est obligatoire', trigger: 'change' },
                    { min: 1, max: 30, message: 'La longueur maximale est de 30 caractères', trigger: 'change' }
                ],
                energy_meter: [
                    { required: true, message: 'La sélection du compteur d\'énergie est obligatoire', trigger: 'change' }
                ],
                energy_meter_name: [
                    { required: true, message: 'Le numéro du compteur d\'énergie est obligatoire', trigger: 'change' },
                    { validator: validateEnergyMeterName, trigger:'blur'}
                ],
                machine_type: [
                    { required: true, message: 'La sélection du type de machine est obligatoire pour définir le Cosphi', trigger: 'change' }
                ],
                power: [
                    { validator: validatePower, trigger: 'change' }
                ],
                tension:[
                    { validator: validateTension, trigger: 'change' }
                ],
                current: [
                    { validator: validateCurrent, trigger: 'change' }
                ],
                speed: [
                    { validator: validateSpeed, trigger: 'change' }
                ],
                cos_phi:[
                    { validator: validateCosPhi, trigger: 'change' }
                ],
                second_phase_balancing_value: [
                    { validator: validateBalancingValue, trigger: 'change' }
                ],
                third_phase_balancing_value: [
                    { validator: validateBalancingValue, trigger: 'change' }
                ],
                additional_power: [
                    { validator: validateAdditionalPower, trigger: 'change' }
                ],
                idle_power: [
                    { validator: validateIdlePower, trigger: 'change' }
                ],
                rules: [
                    { validator: validateRules, trigger: 'change' }
                ]
            },
            equipmentList:[
                { key: 'motor', value: 'Moteur' },
                { key: 'machine', value: 'Machine Industrielle' }
            ],
            motorList: [
                { key: 'asynchronous', value: 'Asynchrone' },
                { key: 'synchronous', value: 'Synchrone' },
                { key: 'continuous', value: 'Courant Continu' }
            ],
        };
    },
    computed: {
        ...mapGetters([
            'currentSite',
            'currentArea',
            'currentEquipment',
            'userIsAtLeastManager',
            'energyMetersEquipment',
            'energyMetersPrefix',
            'currentEnergyMeterNames'
        ]),
        toLoad(){
            return [
                {
                    name: 'equipments',
                    model: 'equipment',
                    filters: {
                        parent_site_uid: this.currentSite.uid
                    }
                },

            ];
        },
        energyMeterFullName(){
            return this.energyMetersPrefix(this.form.energy_meter) + this.form.energy_meter_name
        },
        equipmentType(){
            return this.form.equipment_type || 'motor';
        },
        parentAreaUid() {
            return this.$route.params.parentAreaUid || null;
        },
        equipmentTypeVerbose () {
            return this.equipmentType === 'motor' ? ' moteur' : 'machine';
        },
        commonTypeVerbose () {
            return {
                article1: this.equipmentType === 'motor' ? ' de l\'' : 'de la',
                article2: this.equipmentType === 'motor' ? 'un' : 'une',
                new: this.equipmentType === 'motor' ? ' nouveau' : 'nouvelle',
                success: this.equipmentType === 'motor' ? 'Moteur enregistré avec succès' : 'Machine enregistrée avec succès'
            }
        },
        usesVirtualEnergyMeter () {
            return this.form.energy_meter === 'virtual_meter';
        },
        usesWattMeter () {
            return this.form.energy_meter.toLowerCase().includes('wattmeter');
        },
        pageTitle () {
            return this.currentEquipment ? this.currentEquipment.name : `${this.commonTypeVerbose.new} ${this.equipmentTypeVerbose}`;
        },
        pageDescription () {
            return this.currentEquipment ? `Modifer les informations de ${this.currentEquipment.name}` : `Créer ${this.commonTypeVerbose.article2} ${this.commonTypeVerbose.new} ${this.equipmentTypeVerbose}`;
        },
    },
    mounted () {
        if(!this.currentSite){
            this.leaveSite();
            return;
        }
    },
    methods: {
        capitalizeFirstLetter,
        onDataFetched(data){
            this.$set(this, 'equipments', data.equipments);
            const isEditing = !!this.currentEquipment;

            let parentUid = null;
            if (isEditing && this.currentEquipment && this.currentEquipment.parent_area_uid) {
                parentUid = this.currentEquipment.parent_area_uid;
            } else if (this.currentArea && this.currentArea.uid) {
                this.parent = this.currentArea.uid;
            } else if (this.parentAreaUid) {
                parentUid = this.parentAreaUid;
            }

            if (isEditing) {
                this.setForm({...this.currentEquipment});
            } else {
                this.form.parent_site_uid = this.currentSite.uid;
            }

            if (parentUid) {
                this.setParent(parentUid);
                this.setSiblings(parentUid);
            }
        },
        setParent(uid){
            this.$store.dispatch('remoteGet', {
                url: `area/${uid}`,
            }).then(res => {
                this.$set(this, 'parent', res.data);
            }).catch(err => {
                this.$notify({
                    title: 'Erreur',
                    message: err.message,
                    type: 'error'
                });
            });
        },
        setSiblings(parentUid){
            if (!Array.isArray(this.equipments)) {
                this.$set(this, 'siblings', []);
                return;
            }
            const currentUid = this.currentEquipment ? this.currentEquipment.uid : null;
            this.$set(
                this,
                'siblings',
                this.equipments.filter(eq => eq.parent_area_uid === parentUid && eq.uid !== currentUid && eq.archived === false)
            );
        },
        leaveSite(){
            if(this.currentEquipment){
                this.$store.commit('UNSET_CURRENT_EQUIPMENT');
            }
            this.$router.go(-1);
        },
        setForm(data){
            this.$set(this, 'form', data);
            this.form.parent_site_uid = this.currentSite.uid;
            this.form.energy_meter_name = this.getEnergyMeterNumber(data.energy_meter_name)
            this.form.second_phase_balancing_value = data.second_phase_balancing_value * 100;
            this.form.third_phase_balancing_value = data.third_phase_balancing_value * 100;
        },
        getEnergyMeterNumber(energyMeterName) {
            const match = energyMeterName.match(/\d+$/);
            return match ? parseInt(match[0]) : null;
        },
        resetEquipmentTypeRelatedFields(type) {
            if (type === 'motor') {
                this.form.cos_phi_known = true;
                this.form.machine_type = '';
                this.form.cos_phi = 0.0;
            }else{
                this.form.cos_phi_known = true;
                this.form.cos_phi = 0.0;
                this.form.motor_type = '';
                this.form.current = 0;
                this.form.speed = 0;
            }
        },
        resetCosphiRelatedFields() {
                this.form.cos_phi = 0.0;
                this.form.machine_type = '';
        },
        resetEnergyMeterRelatedFields(data){
            if(data === 'virtual_meter'){
                this.form.rules = '';
                this.form.tension = 0;
                this.form.power = 0;
                this.form.current = 0;
                this.form.speed = 0;
                this.form.cos_phi = 0.0;
            }else if(data.toLowerCase().includes('wattmeter')){
                this.form.rules = '';
                this.form.tension = 0;
                this.form.power = 0;
                this.form.current = 0;
                this.form.speed = 0;
                this.form.cos_phi = 0.0;
                this.form.second_phase_balancing_value = 0.0;
                this.form.third_phase_balancing_value = 0.0;
                this.form.additional_power = 0;
            }else{
                this.form.rules = '';
            }
        },
        updateCosPhi(selectedKey) {
            const selected = this.$store.state.cosphiLibrary.find(
                item => item.key === selectedKey
            );
            if (selected) {
                this.form.cos_phi = selected.value;
            } else {
                this.form.cos_phi = null;
            }
        },
        async save () {
            this.$refs.equipmentForm.validate( valid => {
                if (valid) {
                    this.$set(this, 'loading', true);
                    // save in concrete
                    let action = this.currentEquipment ? 'patchItem' : 'createItem';

                    let dataToPost = { ...this.form };
                    if (this.currentEquipment) {
                        dataToPost.uid = this.currentEquipment.uid;
                        dataToPost.url = this.currentEquipment.url;
                    }else{
                        dataToPost.parent_area_uid = this.parentAreaUid
                    }
                    dataToPost.energy_meter_name = this.energyMeterFullName;
                    dataToPost.second_phase_balancing_value = dataToPost.second_phase_balancing_value / 100;
                    dataToPost.third_phase_balancing_value = dataToPost.third_phase_balancing_value / 100;
                    let declarationUpdated = false;
                    if (action === 'patchItem' && this.currentEquipment) {
                        const fields = this.$store.state.equipmentDeclarationFields || [];
                        declarationUpdated = fields.some(field => this.currentEquipment[field] !== dataToPost[field]);
                    }
                    this.$store.dispatch(action, {
                        item: dataToPost,
                        model: 'equipment',
                        url: 'equipment/'
                    // eslint-disable-next-line
                    }).then(async (res) => {
                        if (action === 'patchItem') {
                            await this.$store.dispatch('resetSimulationData', {
                                item: dataToPost,
                                itemType: 'equipment',
                                declarationUpdated: declarationUpdated
                            });
                        }
                        this.$notify({
                            title: 'Enregistré',
                            message: 'Site enregistré avec succès',
                            type: 'success'
                        });
                        this.$set(this, 'loading', false);
                        this.leaveSite();
                    }).catch(err => {
                        this.$set(this, 'loading', false);
                        this.$notify({
                            title: 'Erreur',
                            message: err.message,
                            type: 'error'
                        });
                    });
                } else {
                    this.$set(this, 'loading', false);
                    this.$notify({
                        title: 'Erreur',
                        message: 'Merci de vérifiez que les champs sont bien renseignés',
                        type: 'error'
                    });
                }
            });
        }
    }
};
</script>

<style lang="scss">
.equipment-form {
    .form-container{
        .el-form.site-form {
            display: flex;
            flex-wrap: wrap;
            flex-direction: column;;
            gap: 3rem;
            .form-part {
                flex-basis: calc(50% - 1.5rem);
                .part{
                    display: flex;
                    justify-content: space-evenly;
                    flex-wrap: wrap;
                }
                .bottom-part{
                    flex-direction: column;
                    .sub-part {
                        align-items: flex-end;
                        justify-content: center;
                        gap: 3rem;
                    }
                }
            }
        }
        .button-container{
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
        }
    }
    
}

.equipment-form-number-label {
    margin: 0;
    padding: 0;
}
.equipment-form-number-input{
    margin-top:  1em;
}
.equipment-form-help {
    margin-top: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/page/Synoptic/Equipments/EquipmentForm.vue